import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { TeacherItem, CreateTeacherRequest } from '../../types';
import { retreatService } from '../../services/retreatService';

interface TeacherState {
  teachers: TeacherItem[];
  loading: boolean;
  error: string | null;
}

const initialState: TeacherState = {
  teachers: [],
  loading: false,
  error: null,
};

// Async thunks
export const fetchTeachers = createAsyncThunk(
  'teacher/fetchTeachers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await retreatService.getTeachers();
      return response.data.data; // Extract the actual data array from the paginated response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch teachers');
    }
  }
);

export const createTeacher = createAsyncThunk(
  'teacher/createTeacher',
  async (teacherData: CreateTeacherRequest, { rejectWithValue }) => {
    try {
      const response = await retreatService.createTeacher(teacherData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create teacher');
    }
  }
);

const teacherSlice = createSlice({
  name: 'teacher',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetTeachers: (state) => {
      state.teachers = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch teachers
      .addCase(fetchTeachers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTeachers.fulfilled, (state, action) => {
        state.loading = false;
        state.teachers = action.payload;
      })
      .addCase(fetchTeachers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create teacher
      .addCase(createTeacher.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTeacher.fulfilled, (state, action) => {
        state.loading = false;
        state.teachers.push(action.payload);
      })
      .addCase(createTeacher.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetTeachers } = teacherSlice.actions;
export default teacherSlice.reducer;
